import { createFileRoute, Link } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@/lib/server-fn-compat";
import {
  createNewsArticle,
  deleteNewsArticle,
  getNewsArticleAdmin,
  listAllNewsArticles,
  updateNewsArticle,
} from "@/lib/news.functions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/admin/RichTextEditor";
import { MediaField } from "@/components/admin/MediaField";
import { MEDIA_TARGETS } from "@/lib/media-buckets";
import { ARTICLE_BODY_CLASS } from "@/lib/article-parts";
// The .ai-article rules ARTICLE_BODY_CLASS relies on. The public article route
// loads them too, but that chunk is never on the admin screen.
import "@/lib/article-clean.css";
import { listAiaMembers } from "@/lib/aia.functions";
import { fetchEmailBoardSnippet } from "@/lib/board-snippet.queries";
import { publicSettingsQueryOptions, settingsQueryOptions } from "@/lib/settings.queries";
import {
  buildArticleEmailHtml,
  DEFAULT_HERO_HEIGHT,
  isEmailLayout,
  probeHeroHeight,
  type EmailArticle,
} from "@/lib/article-email";
import { openEmailPreviewShell, writeEmailPreview } from "@/lib/email-preview-window";
import { PREVIEW_KEY, type ArticlePreviewDraft } from "@/routes/news_.preview";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Mail, ExternalLink, Eye, FileCode2, Newspaper } from "lucide-react";
import { Field } from "@/components/admin/Field";
import { FormDialog, FormSection, SaveButton, useDirty } from "@/components/admin/FormDialog";

export const Route = createFileRoute("/_authenticated/admin/news")({
  component: NewsAdmin,
});

type Article = {
  id: string;
  title: string;
  slug: string;
  image_url: string | null;
  header_image_url: string | null;
  /** Alt text for the list/header image (migration 024). */
  image_alt: string | null;
  excerpt: string | null;
  /** NOT body_html — the list is metadata only (see listAllNewsArticles).
      Open the edit dialog to fetch a body. */
  has_body: boolean;
  external_url: string | null;
  category: string | null;
  published_at: string;
  is_active: boolean;
  has_newsletter: boolean;
};

type FormState = {
  id?: string;
  title: string;
  slug: string;
  image_url: string;
  header_image_url: string;
  image_alt: string;
  excerpt: string;
  body_html: string;
  external_url: string;
  category: string;
  published_at: string;
  is_active: boolean;
  /** No longer editable in the form (the toggle was removed). Loaded in
      openEdit and sent back unchanged: updateNewsArticle writes every column
      and its zod schema defaults a missing has_newsletter to false, so
      dropping it from the payload would clear the flag on every save. */
  has_newsletter: boolean;
  /** Carried through saves untouched — null = auto-detect, 'email' = iframe,
      'clean' = native site rendering + live advisory-board footer. */
  body_format: "email" | "clean" | null;
  /** news_articles.show_advisory_board (migration 034). Read from the full
      row in openEdit (the list query does not carry it); a row without the
      column yet counts as true. false = no board in the newsletter or on the
      article page. */
  show_advisory_board: boolean;
};

const emptyForm: FormState = {
  title: "",
  slug: "",
  image_url: "",
  header_image_url: "",
  image_alt: "",
  excerpt: "",
  body_html: "",
  external_url: "",
  category: "Aesthetic Insights",
  published_at: new Date().toISOString().slice(0, 10),
  is_active: true,
  has_newsletter: false,
  body_format: null,
  show_advisory_board: true,
};

function slugify(s: string) {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .slice(0, 180);
}

function NewsAdmin() {
  const qc = useQueryClient();
  // Contact address + subscribe subject for the newsletter masthead (Settings).
  const { data: publicSettings } = useQuery(publicSettingsQueryOptions());
  const list = useServerFn(listAllNewsArticles);
  const create = useServerFn(createNewsArticle);
  const update = useServerFn(updateNewsArticle);
  const del = useServerFn(deleteNewsArticle);
  const getFull = useServerFn(getNewsArticleAdmin);

  const { data: rows = [], isLoading } = useQuery({
    queryKey: ["admin_news_articles"],
    queryFn: () => list() as Promise<Article[]>,
  });

  const [open, setOpen] = useState(false);
  // True while a MediaField's library picker (a sibling modal) is up — the
  // edit dialog must ignore outside-interaction closes during that.
  const [pickerBusy, setPickerBusy] = useState(false);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [bodyMode, setBodyMode] = useState<"visual" | "html">("visual");
  /** True while an existing article's body is still being fetched. Saving in
      that window would persist an empty body over the real one. */
  const [bodyLoading, setBodyLoading] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // Snapshot only once the body has landed, so the fetched body does not
  // count as an edit.
  const dirty = useDirty(form, open && !bodyLoading);

  const sorted = useMemo(
    () => [...rows].sort((a, b) => (a.published_at < b.published_at ? 1 : -1)),
    [rows],
  );

  const saveMut = useMutation({
    mutationFn: async (f: FormState) => {
      const payload = {
        title: f.title.trim(),
        slug: f.slug.trim() || slugify(f.title),
        image_url: f.image_url || null,
        header_image_url: f.header_image_url || null,
        image_alt: f.image_alt.trim() || null,
        excerpt: f.excerpt || null,
        body_html: f.body_html || null,
        external_url: f.external_url || null,
        category: f.category || null,
        published_at: f.published_at,
        is_active: f.is_active,
        has_newsletter: f.has_newsletter,
        body_format: f.body_format,
        show_advisory_board: f.show_advisory_board,
      };
      if (f.id) return update({ data: { ...payload, id: f.id } });
      return create({ data: payload });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_news_articles"] });
      qc.invalidateQueries({ queryKey: ["news_articles"] });
      setOpen(false);
      setForm(emptyForm);
      toast.success("Saved");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Save failed"),
  });

  const delMut = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin_news_articles"] });
      qc.invalidateQueries({ queryKey: ["news_articles"] });
      toast.success("Removed");
    },
    onError: (e) => toast.error(e instanceof Error ? e.message : "Delete failed"),
  });

  /**
   * The list no longer carries body_html (200+ email-HTML bodies blow the Data
   * API's 1 MB result cap), so the body is fetched for just this article. The
   * dialog opens immediately with the metadata and fills the body in when it
   * lands, rather than making the editor wait on a round trip.
   */
  const openEdit = async (a: Article) => {
    setForm({
      id: a.id,
      title: a.title,
      slug: a.slug,
      image_url: a.image_url ?? "",
      header_image_url: a.header_image_url ?? "",
      image_alt: a.image_alt ?? "",
      excerpt: a.excerpt ?? "",
      body_html: "",
      external_url: a.external_url ?? "",
      category: a.category ?? "",
      published_at: a.published_at?.slice(0, 10) ?? new Date().toISOString().slice(0, 10),
      is_active: a.is_active,
      has_newsletter: a.has_newsletter,
      body_format: null,
      show_advisory_board: true,
    });
    setBodyMode("visual");
    setBodyLoading(true);
    setOpen(true);
    try {
      const full = await getFull({ data: { id: a.id } });
      const body = full?.body_html ?? "";
      const format: FormState["body_format"] =
        full?.body_format === "email" || full?.body_format === "clean"
          ? full.body_format
          : null;
      const showBoard = full?.show_advisory_board !== false;
      setForm((f) =>
        f.id === a.id
          ? { ...f, body_html: body, body_format: format, show_advisory_board: showBoard }
          : f,
      );
      // Email-layout imports open in HTML-source mode so the visual editor
      // cannot silently flatten their table markup on save.
      setBodyMode(isEmailLayout(body) ? "html" : "visual");
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Could not load the article body — do not save.",
      );
    } finally {
      setBodyLoading(false);
    }
  };

  const openNew = () => {
    setForm({ ...emptyForm });
    setBodyMode("visual");
    setBodyLoading(false);
    setOpen(true);
  };

  /**
   * "Preview website": stash the draft in localStorage and open the real
   * site's /news/preview route in a new tab — full site chrome (top bar,
   * theme, footer), not a dialog. Same-origin localStorage carries the
   * unsaved draft across tabs.
   */
  const previewWebsite = () => {
    if (!form.body_html.trim()) {
      toast.error("The article has no body to preview.");
      return;
    }
    const draft: ArticlePreviewDraft = {
      title: form.title || "Untitled",
      excerpt: form.excerpt || null,
      body_html: form.body_html,
      published_at: form.published_at,
      body_format: form.body_format,
      image_url: form.image_url || null,
      show_advisory_board: form.show_advisory_board,
    };
    try {
      window.localStorage.setItem(PREVIEW_KEY, JSON.stringify(draft));
    } catch {
      toast.error("Could not stage the preview (storage unavailable).");
      return;
    }
    window.open("/news/preview", "_blank");
  };

  /**
   * "Preview email": build an email-client-safe version of the article
   * (table layout, inlined styles, 600px hybrid, MSO conditionals — see
   * lib/article-email.ts) and open it in a mock email-client reading window
   * with width toggles + Download/Copy. The AIA advisory board section comes
   * from the prebuilt fragment-cache snippet (no roster query); if that is
   * missing, the roster is fetched live and baked locally as before. The
   * window opens synchronously (popup blockers) and is populated when the
   * build lands. Both header treatments are built (overlay = title on the
   * hero band, classic = hero then title) so the shell can toggle between
   * them; the overlay band height follows the hero's real aspect ratio.
   */
  const previewEmail = async () => {
    if (!form.body_html.trim()) {
      toast.error("The article has no body to generate from.");
      return;
    }
    const shell = openEmailPreviewShell();
    if (!shell) {
      toast.error("The preview window was blocked — allow pop-ups for this site.");
      return;
    }
    setEmailBusy(true);
    try {
      // Board switched off for this issue: nothing to fetch, and
      // includeBoard: false below drops the whole section.
      let board: string | any[] = [];
      if (form.show_advisory_board) {
        try {
          board = await fetchEmailBoardSnippet();
        } catch {
          board = (((await listAiaMembers()) as any[]) ?? [])
            .filter((m) => m.is_active)
            .sort(
              (a, b) =>
                String(a.last_name).localeCompare(String(b.last_name)) ||
                String(a.first_name).localeCompare(String(b.first_name)),
            );
        }
      }
      const article: EmailArticle = {
        title: form.title || "Untitled",
        slug: form.slug || "article",
        image_url: form.image_url || null,
        header_image_url: form.header_image_url || null,
        excerpt: form.excerpt || null,
        body_html: form.body_html,
        external_url: form.external_url || null,
        category: form.category || null,
        published_at: form.published_at,
      };
      const origin = window.location.origin;
      const heroSrc = article.header_image_url || article.image_url;
      const heroHeight = heroSrc
        ? await probeHeroHeight(
            /^https?:\/\//i.test(heroSrc) ? heroSrc : origin + heroSrc,
          )
        : DEFAULT_HERO_HEIGHT;
      // The subscribe subject lives in the admin-only email group; fall back to
      // the public defaults if that read fails so the preview still builds.
      let subscribeSubject: string | undefined;
      try {
        subscribeSubject = (await qc.fetchQuery(settingsQueryOptions())).email.subscribeSubject;
      } catch {
        subscribeSubject = undefined;
      }
      const contact = {
        email: publicSettings!.org.generalEmail,
        subscribeSubject: subscribeSubject ?? "Sign Me Up",
      };
      const includeBoard = form.show_advisory_board;
      const overlay = buildArticleEmailHtml(article, board, origin, {
        header: "overlay",
        heroHeight,
        contact,
        includeBoard,
      });
      const classic = buildArticleEmailHtml(article, board, origin, {
        header: "classic",
        contact,
        includeBoard,
      });
      writeEmailPreview(
        shell,
        { overlay, classic },
        { title: article.title, slug: article.slug },
      );
    } catch (e) {
      shell.close();
      toast.error(e instanceof Error ? e.message : "Could not generate the newsletter HTML");
    } finally {
      setEmailBusy(false);
    }
  };

  /**
   * Whether the site will render this body natively (NativeArticle) rather
   * than in the sandboxed email iframe - the same decision as resolveFormat()
   * in routes/news_.$slug.tsx: an explicit body_format wins, otherwise sniff.
   * Only native bodies get the article column in the editor; an email layout
   * carries its own CSS, which no editor surface can mirror.
   */
  const rendersAsArticle =
    form.body_format === "clean" ||
    (form.body_format !== "email" && !isEmailLayout(form.body_html));

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>News & Articles</CardTitle>
          <CardDescription>
            Manage Aesthetic Insights articles. Articles with a newsletter can be previewed
            and sent as email samples.
          </CardDescription>
        </div>
        <Button size="sm" onClick={openNew}>
          <Plus className="mr-1 h-4 w-4" /> New article
        </Button>
        {/* The library picker is a sibling modal stacked over this dialog —
            `busy` keeps its outside-clicks from closing it. (Previews open in
            their own windows and never touch this dialog.) */}
        <FormDialog
          open={open}
          onOpenChange={setOpen}
          size="xl"
          icon={Newspaper}
          title={form.id ? "Edit article" : "New article"}
          context={form.id ? form.title : "New Aesthetic Insights article"}
          dirty={dirty}
          busy={pickerBusy || saveMut.isPending}
          asForm
          onSubmit={() => {
            if (!bodyLoading) saveMut.mutate(form);
          }}
          footer={
            <>
              {/* Two previews, each in its own window, disabled while the
                  body is still loading: website (full site with the draft
                  in the content area) and email (mock client reading pane
                  with Download/Copy for Outlook/Gmail tests). */}
              <div className="flex flex-wrap gap-2 sm:mr-auto">
                <Button
                  type="button"
                  variant="outline"
                  disabled={bodyLoading || !form.body_html}
                  onClick={previewWebsite}
                >
                  <Eye className="mr-1 h-4 w-4" /> Preview website
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  disabled={bodyLoading || emailBusy || !form.body_html}
                  onClick={previewEmail}
                >
                  <FileCode2 className="mr-1 h-4 w-4" />
                  {emailBusy ? "Generating…" : "Preview email"}
                </Button>
              </div>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              {/* Blocked while the body is in flight — saving then would
                  overwrite the stored body with an empty string. */}
              <SaveButton
                dirty={dirty}
                pending={saveMut.isPending}
                disabled={bodyLoading}
                label={bodyLoading ? "Loading…" : "Save"}
              />
            </>
          }
        >
          <div className="space-y-4">
            <FormSection title="Article">
              <Field label="Title" required full>
                <Input
                  /* Capped: a full-width field at the xl size is wider than is
                     comfortable to read back. */
                  className="max-w-3xl"
                  required
                  value={form.title}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      title: e.target.value,
                      slug: f.id ? f.slug : slugify(e.target.value),
                    }))
                  }
                />
              </Field>
              {/* Slug 50% / Published 25% / Active 25%. The body grid is two
                  columns, so this row is a 4-column sub-grid spanning both;
                  it collapses to one column on phones like every other Field. */}
              <div className="grid gap-4 sm:col-span-2 sm:grid-cols-4">
                <div className="sm:col-span-2">
                  <Field label="Slug" required>
                    <Input
                      required
                      value={form.slug}
                      onChange={(e) => setForm({ ...form, slug: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-1">
                  <Field label="Published">
                    <Input
                      type="date"
                      required
                      value={form.published_at}
                      onChange={(e) => setForm({ ...form, published_at: e.target.value })}
                    />
                  </Field>
                </div>
                <div className="sm:col-span-1">
                  <Field label="Active">
                    <div className="flex items-center gap-2 rounded-md border bg-muted/30 px-3 py-2">
                      <Switch
                        id="na-active"
                        checked={form.is_active}
                        onCheckedChange={(v: boolean) => setForm({ ...form, is_active: v })}
                      />
                      <Label htmlFor="na-active" className="cursor-pointer truncate">
                        {form.is_active ? "Visible" : "Hidden"}
                      </Label>
                    </div>
                  </Field>
                </div>
              </div>
              <Field label="Category">
                <Input
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                />
              </Field>
              <Field label="External URL (original source)">
                <Input
                  value={form.external_url}
                  onChange={(e) => setForm({ ...form, external_url: e.target.value })}
                  placeholder="https://…"
                />
              </Field>
            </FormSection>

            <FormSection title="Images">
              <div className="sm:col-span-2">
                <MediaField
                  label="List / thumbnail image (shown in article lists)"
                  value={form.image_url}
                  target={MEDIA_TARGETS.newsImage}
                  onChange={(url, asset) =>
                    setForm((f) => ({
                      ...f,
                      image_url: url,
                      // Seed the alt text from the library row, never overwrite
                      // what the editor already typed.
                      image_alt: f.image_alt || asset?.alt_text || "",
                    }))
                  }
                  onPickerOpenChange={setPickerBusy}
                />
              </div>
              <Field label="Image alt text" full>
                <Input
                  value={form.image_alt}
                  onChange={(e) => setForm({ ...form, image_alt: e.target.value })}
                  placeholder="Describe the image for screen readers and email clients"
                  maxLength={500}
                />
              </Field>
              <div className="sm:col-span-2">
                <MediaField
                  label="Header image (top of the article & newsletter)"
                  value={form.header_image_url}
                  target={MEDIA_TARGETS.newsImage}
                  onChange={(url, asset) =>
                    setForm((f) => ({
                      ...f,
                      header_image_url: url,
                      image_alt: f.image_alt || asset?.alt_text || "",
                    }))
                  }
                  onPickerOpenChange={setPickerBusy}
                  hint="Leave blank to reuse the list image."
                />
              </div>
            </FormSection>

            <FormSection title="Content" cols={1}>
              <Field label="Excerpt">
                <Textarea
                  className="max-w-3xl"
                  rows={3}
                  value={form.excerpt}
                  onChange={(e) => setForm({ ...form, excerpt: e.target.value })}
                />
              </Field>
              <Field label="Body (used on the article page and in the newsletter)">
                <div className="mb-2 flex items-center gap-2">
                  <Button
                    type="button"
                    variant={bodyMode === "visual" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setBodyMode("visual")}
                  >
                    Visual
                  </Button>
                  <Button
                    type="button"
                    variant={bodyMode === "html" ? "secondary" : "ghost"}
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setBodyMode("html")}
                  >
                    HTML source
                  </Button>
                  {isEmailLayout(form.body_html) && bodyMode === "visual" && (
                    <span className="text-xs text-amber-600">
                      Imported newsletter layout — use HTML source; the visual editor would
                      restructure its tables.
                    </span>
                  )}
                </div>
                {bodyMode === "visual" ? (
                  <RichTextEditor
                    value={form.body_html}
                    onChange={(html) => setForm({ ...form, body_html: html })}
                    uploadFolder="news"
                    minHeight={240}
                    placeholder="Write the article body…"
                    contentClassName={rendersAsArticle ? ARTICLE_BODY_CLASS : undefined}
                    layouts
                  />
                ) : (
                  <Textarea
                    rows={18}
                    className="font-mono text-xs"
                    value={form.body_html}
                    onChange={(e) => setForm({ ...form, body_html: e.target.value })}
                    placeholder="<p>Raw HTML…</p>"
                  />
                )}
              </Field>
            </FormSection>

            <FormSection title="Newsletter blocks" cols={1}>
              <div className="flex items-start gap-3 rounded-md border bg-muted/30 px-3 py-2">
                <Switch
                  id="na-board"
                  className="mt-0.5"
                  checked={form.show_advisory_board}
                  onCheckedChange={(v: boolean) => setForm({ ...form, show_advisory_board: v })}
                />
                <div className="min-w-0 space-y-0.5">
                  <Label htmlFor="na-board" className="cursor-pointer">
                    Include AIA Advisory Board
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Turn off to leave the board block out of this issue's newsletter and article
                    page.
                  </p>
                  {!form.show_advisory_board && isEmailLayout(form.body_html) && (
                    <p className="text-xs text-amber-600">
                      This imported newsletter layout has the board built into its HTML, so the
                      switch cannot remove it. Edit the HTML source instead.
                    </p>
                  )}
                </div>
              </div>
            </FormSection>
          </div>
        </FormDialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Image</TableHead>
              <TableHead>Title</TableHead>
              <TableHead className="w-28">Published</TableHead>
              <TableHead className="w-28 text-center">Active</TableHead>
              <TableHead className="w-32 text-center">Newsletter</TableHead>
              <TableHead className="w-44 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sorted.map((a) => (
              <TableRow key={a.id} className={a.is_active ? "" : "opacity-60"}>
                <TableCell>
                  {a.image_url ? (
                    <img
                      src={a.image_url}
                      alt=""
                      className="h-10 w-14 rounded object-cover"
                    />
                  ) : (
                    <div className="h-10 w-14 rounded bg-muted" />
                  )}
                </TableCell>
                <TableCell>
                  <div className="font-medium">{a.title}</div>
                  <div className="text-xs text-muted-foreground">/{a.slug}</div>
                </TableCell>
                <TableCell className="text-muted-foreground text-sm">
                  {a.published_at?.slice(0, 10)}
                </TableCell>
                <TableCell className="text-center">
                  <Badge variant={a.is_active ? "default" : "secondary"}>
                    {a.is_active ? "Active" : "Hidden"}
                  </Badge>
                </TableCell>
                <TableCell className="text-center">
                  {a.has_newsletter ? (
                    <Badge variant="default">Ready</Badge>
                  ) : (
                    <Badge variant="outline">—</Badge>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {a.has_newsletter && (
                    <Button
                      asChild
                      variant="ghost"
                      size="icon"
                      title="Preview & send newsletter"
                    >
                      <Link to="/newsletter/$slug" params={{ slug: a.slug }} target="_blank">
                        <Mail className="h-4 w-4" />
                      </Link>
                    </Button>
                  )}
                  {a.external_url && (
                    <Button asChild variant="ghost" size="icon" title="Open source">
                      <a href={a.external_url} target="_blank" rel="noreferrer">
                        <ExternalLink className="h-4 w-4" />
                      </a>
                    </Button>
                  )}
                  <Button variant="ghost" size="icon" onClick={() => openEdit(a)}>
                    <Pencil className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      if (confirm(`Delete "${a.title}"?`)) delMut.mutate(a.id);
                    }}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!isLoading && sorted.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className="text-center text-muted-foreground">
                  No articles yet.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

